/**
 * @file index.tsx
 *
 * @fileoverview Renders blog search layout and blogs' categories.
 */

import { FormattedMessage, useIntl } from 'react-intl';
import Layout from '~/jobiqo/components/Layout';
import Pagination from '~/jobiqo/components/Pager';
import BlogSearchForm from '~/jobiqo/components/BlogSearch/components/Form';
import { useRouter } from 'next/router';
import BlogSearchNoResultsFound from '~/jobiqo/components/BlogSearch/components/NoResultsFound';
import { BlogPost, BlogSearchResult, BlogTag } from '~/jobiqo/models/blog';
import BlogSearchMetatags from '~/jobiqo/components/BlogSearch/components/Metatags';
import { useContext } from 'react';
import BlogSearchLoading from '~/jobiqo/components/BlogSearch/components/Loading';
import { Term } from '~/jobiqo/types/types';
import AdUnitBlogSearchSidebarSlot from '~/jobiqo/components/Slots/AdUnitBlogSearchSidebarSlot';
import { BLOGS_PER_PAGE } from '~/jobiqo/pages/blogs';
import BlogPopularTags from '~/jobiqo/components/BlogSearch/components/PopularTags';
import BlogSearchBadges from '~/jobiqo/components/BlogSearch/components/Badges';
import SEOSearchTitle from '../Search/SeoSearchTitle';
import { LayoutContext } from '~/jobiqo/context/layout';
import BlogSearchBlogTeaserCell from '~/jobiqo/components/BlogSearch/components/BlogTeaserCell';
import BlogSearchFeaturedBlogs from '~/jobiqo/components/BlogSearch/components/FeaturedBlogs';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { OneSidebarLayout } from '~/vcl/templates/01-layout/02-grid-layouts/02-one-sidebar';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { HeroRow } from '~/vcl/organisms/04-landingpage/02-hero-row';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import SEOSearchFooter from '~/jobiqo/components/Search/SeoSearchFooter';
import BlogSearchBanner from '~/jobiqo/components/BlogSearch/components/Banner';

export interface BlogSearchProps {
  /**
   * The available categories.
   */
  categories: Term[];
  /**
   * The query data.
   */
  blogSearchResult: BlogSearchResult;
  /**
   * The error flag.
   */
  error: boolean;
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The current category.
   */
  currentCategory?: Term;
  /**
   * The current tag.
   */
  currentTag?: Term;
  /**
   * The popular tags.
   */
  popularTags: BlogTag[];
  /**
   * The featured blogs.
   */
  featuredBlogs: BlogPost[];
}

export default function BlogSearch({
  categories,
  blogSearchResult,
  error,
  loading,
  currentCategory,
  currentTag,
  popularTags,
  featuredBlogs,
}: BlogSearchProps) {
  const { getSidenavCollapsed } = useContext(LayoutContext);
  const intl = useIntl();
  const router = useRouter();
  let ErrorList: React.ReactNode = null;

  if (loading) {
    return <BlogSearchLoading />;
  }

  if (error) {
    ErrorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="blog_search_error"
          defaultMessage="There was an error searching blogs."
        />
      </MessageAlert>
    );
  }

  // The only thing right now in the sidebar are ads. if the site has no ads
  // We should render a smaller centered layout.
  const SidebarAds = <AdUnitBlogSearchSidebarSlot />;
  const emptySidebar = SidebarAds.type() === null && popularTags.length === 0;

  const searchForm = (
    <BlogSearchForm
      onSubmit={(keyword: string) => {
        router.push({
          pathname: router.pathname,
          query: {
            ...(router.query.category && {
              category: router.query.category,
            }),
            ...(router.query.tagName && {
              tagName: router.query.tagName,
            }),
            ...(keyword && {
              search: keyword,
            }),
          },
        });
      }}
    />
  );

  // Only show featured blogs if no search criterias are set.
  const FeaturedBlogsSection = router.asPath === '/blog' && (
    <BlogSearchFeaturedBlogs blogs={featuredBlogs} />
  );

  return (
    <Layout fullWidthContainer={true}>
      {blogSearchResult.seoSearchContent?.headerImage ? (
        <HeroRow
          image={
            blogSearchResult.seoSearchContent?.headerImage ? (
              <BlogSearchBanner
                image={blogSearchResult.seoSearchContent.headerImage}
              />
            ) : null
          }
        >
          <Main>
            <Text
              textAlign="center"
              tag="h1"
              data-test-id="blog-h1"
              lineHeight={[6, 8]}
              fontSize={[6, 8]}
            >
              {blogSearchResult.seoSearchContent?.title}
            </Text>
            <Spacing size="medium" />
            <Text textAlign="center" fontSize={4} fontWeight="bold">
              {blogSearchResult.seoSearchContent?.subtitle}
            </Text>
            <Spacing size="large" />
            {searchForm}
            <Spacing size="medium" />
            <BlogSearchBadges
              categories={categories}
              tag={currentTag}
              currentCategory={currentCategory}
            />
          </Main>
        </HeroRow>
      ) : (
        <Main>
          <Spacing size="large" />
          {searchForm}
          <Spacing size="medium" />
          <BlogSearchBadges
            categories={categories}
            tag={currentTag}
            currentCategory={currentCategory}
          />
        </Main>
      )}
      <Main {...(!emptySidebar ? { size: 'xLarge' } : null)}>
        {ErrorList}
        <Spacing size="large" />
        {FeaturedBlogsSection}
        <Spacing size="large" />
        <SEOSearchTitle
          title={intl.formatMessage({
            id: 'blog_search_title_type',
            defaultMessage: 'article',
          })}
          searchResult={blogSearchResult}
        />
        {blogSearchResult && (
          <BlogSearchMetatags blogSearchResult={blogSearchResult} />
        )}
        <Spacing size="medium" />
        {blogSearchResult?.result_count === 0 ? (
          <BlogSearchNoResultsFound />
        ) : (
          <OneSidebarLayout
            sidebarPosition="right"
            sidebar={
              emptySidebar ? null : (
                <Spacing marginTop={7}>
                  <BlogPopularTags tags={popularTags} />
                  {SidebarAds}
                </Spacing>
              )
            }
          >
            <Grid
              columns={[1, 2, emptySidebar || getSidenavCollapsed() ? 3 : 2]}
            >
              {blogSearchResult?.posts.map((blogPost) => (
                <BlogSearchBlogTeaserCell
                  key={blogPost.id}
                  blogPost={blogPost}
                />
              ))}
            </Grid>
          </OneSidebarLayout>
        )}
        {blogSearchResult?.result_count > BLOGS_PER_PAGE && (
          <>
            <Spacing size="medium" />
            <Pagination
              items={blogSearchResult?.result_count}
              itemsPerPage={BLOGS_PER_PAGE}
            />
          </>
        )}
        {blogSearchResult?.seoSearchContent?.footer ? (
          <div>
            <Box padding={9}>
              <SEOSearchFooter
                id="footer"
                format={blogSearchResult?.seoSearchContent?.footerFormat}
                content={
                  blogSearchResult?.seoSearchContent?.footer
                    ? blogSearchResult?.seoSearchContent?.footer
                    : ''
                }
              />
            </Box>
          </div>
        ) : null}
      </Main>

      <Spacing marginTop={[8, 12]} />
    </Layout>
  );
}

export { BlogSearch };
